import React from "react";
import FlexBox from "view/components/Layout/FlexBox";
import {Box} from "@material-ui/core";
import clsx from "clsx";
import Columns, {ColumnProps} from "./Columns";
import Rows, {RowsProps} from "./Rows";
import useStyles from "./styles";

export type GridProps = ColumnProps &
  RowsProps & {
    includeRowTitles?: boolean;
    isResponseMode?: boolean;
    isJustSavedDraft?: React.MutableRefObject<boolean>;
    openParagraph?: (rowIndex: number, columnIndex: number) => void;
    headCellSize?: "small" | "big";
  };

function Grid({
  viewOnly,
  activeCells,
  cellsBeforeMarkingMandatory,
  setAreAllCellsMandatory,
  openSettings,
  areAllCellsMandatory,
  includeRowTitles: includeRowTitlesProps,
  isResponseMode,
  openParagraph,
  task,
  customRemoveRow,
  removeColumnField,
  isJustSavedDraft,
  headCellSize = "big",
  removeRowField,
  errorCells,
}: GridProps) {
  const classes = useStyles();

  // `includeRowTitles === false` is the "ONLY COLUMNS" table mode — a
  // MEANINGFUL value, not "unset" — so it resolves with `??`, never `||`.
  // Resolved ONCE here and threaded into `Columns` / `Rows`: each of the three
  // used to recompute it with its own operator (`||` here and in `Columns`,
  // `??` in `Rows`), so an only-columns table whose form default still said
  // `true` rendered the row-title header column in `Columns` while `Rows`
  // rendered no title cells — the reported "row names are back" grid.
  // `undefined` (no prop, no live definition) is passed through so the children
  // can still fall back to their form value.
  const includeRowTitles =
    includeRowTitlesProps ?? task?.gridSchema?.includeRowTitles;

  const showSmallRowTitles =
    isResponseMode &&
    includeRowTitles &&
    !task?.gridSchema?.rows?.some((row) => !!row.name);

  // In `viewOnly` mode the body row-title column drops to 200/270
  // (`viewOnlyRootHeadCell` / `viewOnlyHiddingBox`) to match the data columns,
  // which have no index / mandatory / delete affordances to make room for.
  // The header's inline width must follow `viewOnly` too — otherwise the title
  // header stays 220/290 while its body cell is 200/270, and every data column
  // header ends up shifted ~20px right of its cell (the reported misalignment).
  const isBigHeadCell = headCellSize === "big" && !viewOnly;
  const headCellMinWidth = isBigHeadCell ? "220px" : "200px";
  const headCellMaxWidth = isBigHeadCell ? "290px" : "270px";

  const widthStyles = {
    minWidth: isResponseMode ? headCellMinWidth : undefined,
    maxWidth: isResponseMode ? headCellMaxWidth : undefined,
  };

  return (
    <Box className={classes.grid}>
      <FlexBox>
        {includeRowTitles && !showSmallRowTitles && (
          <Box
            className={clsx(
              classes.hiddingBox,
              viewOnly && classes.viewOnlyHiddingBox
            )}
            style={widthStyles}
          >
            {!viewOnly && !isResponseMode && (
              <Box className={classes.uponLeftCell} />
            )}
            <FlexBox height="100%">
              {!viewOnly && !isResponseMode && (
                <Box className={classes.marginBox} />
              )}
              <Box
                style={{
                  maxHeight: "100%",
                  height: viewOnly || isResponseMode ? "100%" : undefined,
                  ...widthStyles,
                }}
                className={classes.headCell}
              />
            </FlexBox>
          </Box>
        )}
        {showSmallRowTitles && <Box className={classes.smallRowTitle} />}
        <Columns
          activeCells={activeCells}
          viewOnly={viewOnly}
          cellsBeforeMarkingMandatory={cellsBeforeMarkingMandatory}
          setAreAllCellsMandatory={setAreAllCellsMandatory}
          isResponseMode={isResponseMode}
          removeColumnField={removeColumnField}
          includeRowTitles={includeRowTitles}
          task={task}
        />
      </FlexBox>
      <Rows
        activeCells={activeCells}
        includeRowTitles={includeRowTitles}
        openSettings={openSettings}
        areAllCellsMandatory={areAllCellsMandatory}
        cellsBeforeMarkingMandatory={cellsBeforeMarkingMandatory}
        setAreAllCellsMandatory={setAreAllCellsMandatory}
        viewOnly={viewOnly}
        isResponseMode={isResponseMode}
        openParagraph={openParagraph}
        isJustSavedDraft={isJustSavedDraft}
        task={task}
        customRemoveRow={customRemoveRow}
        removeRowField={removeRowField}
        errorCells={errorCells}
      />
    </Box>
  );
}

export default Grid;
